// Who is responsible for what, worked out from the runs the page already has.
// The unit of responsibility is a section: it belongs to its own assignees,
// else the checklist's, else "Anyone".
import { runState, sectionProgress, summariseHistory } from 'src/constants/checklistRuns';
import { sectionOwnerList } from 'src/constants/checklistAccess';

export const ANYONE = { id: 'anyone', name: 'Anyone' };

const isFinished = key => key === 'done' || key === 'late';

// Every section of a run with the people it belongs to.
export const runParts = run =>
  ((run && run.sections) || []).map(section => {
    const owners = (sectionOwnerList(run, section) || []).filter(a => a && a.id);
    return { section, owners: owners.length ? owners : [ANYONE] };
  });

// Everyone with work in these runs, by name, with "Anyone" last.
export const peopleInRuns = runs => {
  const seen = new Map();
  (runs || []).forEach(run =>
    runParts(run).forEach(({ owners }) =>
      owners.forEach(o => {
        if (!seen.has(o.id)) seen.set(o.id, { id: o.id, name: o.name || 'Unknown' });
      })
    )
  );
  return Array.from(seen.values()).sort((a, b) =>
    a.id === ANYONE.id ? 1 : b.id === ANYONE.id ? -1 : String(a.name).localeCompare(String(b.name))
  );
};

export const runHasPerson = (run, personId) =>
  runParts(run).some(({ owners }) => owners.some(o => o.id === personId));

export const STATUS_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Overdue', value: 'overdue' },
  { label: 'To do', value: 'todo' },
  { label: 'Done', value: 'done' }
];

export const runStatusGroup = (run, now) => {
  const key = runState(run, now).key;
  if (isFinished(key)) return 'done';
  return key === 'overdue' ? 'overdue' : 'todo';
};

export const filterRuns = (runs, { person, department, status } = {}, now = new Date()) =>
  (runs || []).filter(
    run =>
      (!person || runHasPerson(run, person)) &&
      (!department || run.department === department) &&
      (!status || status === 'all' || runStatusGroup(run, now) === status)
  );

const issuesIn = (section, responses) => {
  const no = (section.items || []).filter(i => responses[i.key] && responses[i.key].status === 'no');
  return { issues: no.length, critical: no.filter(i => i.critical).length };
};

const sortPeople = rows =>
  rows.sort((a, b) => {
    if (a.person.id === ANYONE.id) return 1;
    if (b.person.id === ANYONE.id) return -1;
    return b.overdue - a.overdue || a.percent - b.percent || String(a.person.name).localeCompare(String(b.person.name));
  });

// Today, per person: how far along they are and what's still open.
export const workByPerson = (runs, now = new Date()) => {
  const byId = new Map();
  const row = person => {
    if (!byId.has(person.id)) {
      byId.set(person.id, {
        person: { id: person.id, name: person.name || 'Unknown' },
        runIds: new Set(),
        sections: 0,
        sectionsDone: 0,
        items: 0,
        answered: 0,
        overdue: 0,
        issues: 0,
        critical: 0,
        outstanding: []
      });
    }
    return byId.get(person.id);
  };

  (runs || []).forEach(run => {
    const state = runState(run, now);
    const finished = isFinished(state.key);
    const responses = run.responses || {};
    runParts(run).forEach(({ section, owners }) => {
      const sp = sectionProgress(section, responses);
      const found = issuesIn(section, responses);
      owners.forEach(owner => {
        const w = row(owner);
        w.runIds.add(run._id);
        w.sections += 1;
        w.items += sp.total;
        w.answered += sp.answered;
        w.issues += found.issues;
        w.critical += found.critical;
        if (finished || sp.done) {
          w.sectionsDone += 1;
        } else {
          w.outstanding.push({ run, section, remaining: sp.total - sp.answered, state });
          if (state.key === 'overdue') w.overdue += 1;
        }
      });
    });
  });

  const rank = { overdue: 0, dueSoon: 1, due: 2, anytime: 3, upcoming: 4 };
  return sortPeople(
    Array.from(byId.values()).map(({ runIds, ...w }) => ({
      ...w,
      checklists: runIds.size,
      percent: w.items ? Math.round((w.answered / w.items) * 100) : 0,
      outstanding: w.outstanding.sort(
        (a, b) =>
          (rank[a.state.key] === undefined ? 9 : rank[a.state.key]) -
            (rank[b.state.key] === undefined ? 9 : rank[b.state.key]) ||
          new Date(a.run.dueAt || 8.64e15) - new Date(b.run.dueAt || 8.64e15)
      )
    }))
  );
};

// Runs cut down to one person's sections. When their share is finished but the
// run as a whole isn't (a colleague's section is outstanding), it's marked
// `partDone` so it doesn't count as their miss. If they own every section, an
// unfinished run is still theirs to complete.
export const scopeToPerson = (runs, personId) =>
  (runs || [])
    .map(run => {
      const sections = runParts(run)
        .filter(({ owners }) => owners.some(o => o.id === personId))
        .map(({ section }) => section);
      if (!sections.length) return null;
      const scoped = { ...run, sections };
      const shared = sections.length < (run.sections || []).length;
      if (
        run.status !== 'done' &&
        shared &&
        sections.every(s => sectionProgress(s, run.responses || {}).done)
      ) {
        scoped.partDone = true;
      }
      return scoped;
    })
    .filter(Boolean);

// Over a date range, per person: the same numbers as History shows when that
// person is selected.
export const historyByPerson = (runs, now = new Date(), today = null) =>
  peopleInRuns(runs)
    .map(person => {
      const { totals } = summariseHistory(scopeToPerson(runs, person.id), now, today);
      return { person, ...totals };
    })
    .sort((a, b) => {
      if (a.person.id === ANYONE.id) return 1;
      if (b.person.id === ANYONE.id) return -1;
      const ra = a.completionRate === null ? 101 : a.completionRate;
      const rb = b.completionRate === null ? 101 : b.completionRate;
      return ra - rb || String(a.person.name).localeCompare(String(b.person.name));
    });